import { useState, useEffect, useRef } from "react";
import { Box, Typography, FormControlLabel, Tooltip, Grid, Modal } from "@mui/material";
import { useSelector } from "react-redux";
import CloseIcon from '@mui/icons-material/Close';
import BaseInput from "../../BaseInput";
import BaseSelect from "../../BaseSelect";
import BaseTextareaAutosize from "../../BaseTextareaAutosize";
import BaseButton from "../../BaseButton";
import BaseCheckbox from "../../BaseCheckbox";
import PhoneNumberInput from "../../PhoneNumberInput";
import { isValidPhoneNumber, calculateAge } from "../../../utils/validation";

export default function PhysicianOrderStep({ data, onNext, onBack, onClose }) {
  const auth = useSelector(state => state.auth);

  const [formData, setFormData] = useState({
    patientFirstName: data.patientFirstName || '',
    patientLastName: data.patientLastName || '',
    patientDateOfBirth: data.patientDateOfBirth || '',
    patientSex: data.patientSex || '',
    patientPhone: data.patientPhone || '',
    physicianNotes: data.physicianNotes || '',
    noBoneMarrowTransplant: data.noBoneMarrowTransplant || false,
    noRecentBloodTransfusion: data.noRecentBloodTransfusion || false,
  });

  const [errors, setErrors] = useState({});
  const [showUnderageModal, setShowUnderageModal] = useState(false);
  const firstInputRef = useRef(null);

  // Auto-focus first input on mount
  useEffect(() => {
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, []);

  // Get today's date formatted for display (uses local timezone)
  const todayFormatted = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleNext = () => {
    // Validate required fields
    const newErrors = {};

    if (!formData.patientFirstName.trim()) {
      newErrors.patientFirstName = 'First name is required';
    }

    if (!formData.patientLastName.trim()) {
      newErrors.patientLastName = 'Last name is required';
    }

    if (!formData.patientDateOfBirth) {
      newErrors.patientDateOfBirth = 'Date of birth is required';
    } else {
      // Check if patient is at least 18 years old
      const age = calculateAge(formData.patientDateOfBirth);
      if (age < 18) {
        setShowUnderageModal(true);
        return;
      }
    }

    if (!formData.patientSex) {
      newErrors.patientSex = 'Sex is required';
    }

    if (!formData.patientPhone) {
      newErrors.patientPhone = 'Phone number is required';
    } else if (!isValidPhoneNumber(formData.patientPhone)) {
      newErrors.patientPhone = 'Please enter a valid 10-digit phone number';
    }

    if (!formData.noBoneMarrowTransplant) {
      newErrors.noBoneMarrowTransplant = 'This confirmation is required';
    }

    if (!formData.noRecentBloodTransfusion) {
      newErrors.noRecentBloodTransfusion = 'This confirmation is required';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      // Debug: Log the form data being passed
      const dataToPass = {
        ...formData,
        physicianName: auth?.name || '',
        physicianNpi: auth?.staffProfile?.npi || ''
      };
      console.log('PhysicianOrderStep - Data being passed to next step:', dataToPass);

      // All validations passed, proceed to next step
      onNext(dataToPass);
    }
  };

  const canProceed = formData.noBoneMarrowTransplant &&
                     formData.noRecentBloodTransfusion &&
                     formData.patientFirstName.trim() &&
                     formData.patientLastName.trim() &&
                     formData.patientDateOfBirth &&
                     formData.patientSex &&
                     isValidPhoneNumber(formData.patientPhone);

  return (
    <Box>
      {/* Title */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h1"
          sx={{
            color: '#5A3BC7',
            fontSize: { xs: '24px', sm: '28px' },
            fontWeight: 600,
            mb: 1
          }}
        >
          Physician Order Agreement
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#5B87EF',
            fontSize: '14px'
          }}
        >
          Complete the physician order and patient information
        </Typography>
      </Box>

      {/* Physician Information Display */}
      <Box sx={{ mb: 3, p: 2, borderRadius: '8px', background: '#F6F9FC' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 600 }}>
            Ordering Physician:
          </Typography>
          <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px' }}>
            {auth?.name || 'Not available'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 600 }}>
            NPI:
          </Typography>
          <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px' }}>
            {auth?.staffProfile?.npi || 'Not available'}
          </Typography>
        </Box>
      </Box>

      {/* Patient Information */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            color: '#46545F',
            fontSize: '16px',
            fontWeight: 600,
            mb: 2
          }}
        >
          Patient Information
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 500, mb: 1 }}>
              Patient First Name <span style={{ color: '#E73E3E' }}>*</span>
            </Typography>
            <BaseInput
              ref={firstInputRef}
              fullWidth
              placeholder="First name"
              value={formData.patientFirstName}
              onChange={(e) => handleChange('patientFirstName', e.target.value)}
              error={!!errors.patientFirstName}
              helperText={errors.patientFirstName}
              sx={{
                '& .MuiOutlinedInput-root': {
                  boxShadow: '0px 2px 10px 0px #5B87EF33'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 500, mb: 1 }}>
              Patient Last Name <span style={{ color: '#E73E3E' }}>*</span>
            </Typography>
            <BaseInput
              fullWidth
              placeholder="Last name"
              value={formData.patientLastName}
              onChange={(e) => handleChange('patientLastName', e.target.value)}
              error={!!errors.patientLastName}
              helperText={errors.patientLastName}
              sx={{
                '& .MuiOutlinedInput-root': {
                  boxShadow: '0px 2px 10px 0px #5B87EF33'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 500, mb: 1 }}>
              Patient Date of Birth <span style={{ color: '#E73E3E' }}>*</span>
            </Typography>
            <BaseInput
              fullWidth
              type="date"
              value={formData.patientDateOfBirth}
              onChange={(e) => handleChange('patientDateOfBirth', e.target.value)}
              error={!!errors.patientDateOfBirth}
              helperText={errors.patientDateOfBirth}
              sx={{
                '& .MuiOutlinedInput-root': {
                  boxShadow: '0px 2px 10px 0px #5B87EF33'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 500, mb: 1 }}>
              Patient Sex <span style={{ color: '#E73E3E' }}>*</span>
            </Typography>
            <BaseSelect
              name="patientSex"
              placeholder="Male / Female / Other"
              options={[
                { value: 'MALE', label: 'Male' },
                { value: 'FEMALE', label: 'Female' },
                { value: 'OTHER', label: 'Other' }
              ]}
              value={formData.patientSex}
              onChangeSelect={(value) => handleChange('patientSex', value)}
              error={errors.patientSex}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <PhoneNumberInput
              label="Patient Phone Number"
              name="patientPhone"
              fullWidth
              required
              value={formData.patientPhone}
              onChange={(value) => handleChange('patientPhone', value)}
              error={!!errors.patientPhone}
              message={errors.patientPhone}
              sx={{
                '& .MuiOutlinedInput-root': {
                  boxShadow: '0px 2px 10px 0px #5B87EF33'
                }
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* FOR PHYSICIAN USE ONLY Section */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            color: '#5A3BC7',
            fontSize: '16px',
            fontWeight: 700,
            mb: 0.5
          }}
        >
          Physician Order Agreement
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#E73E3E',
            fontSize: '12px',
            fontWeight: 600,
            mb: 2
          }}
        >
          FOR PHYSICIAN USE ONLY
        </Typography>
        
        <Box
          sx={{
            p: 2,
            borderRadius: '8px',
            border: '2px solid #5A3BC7',
            background: '#F6F9FC'
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 600 }}>
                Reason for testing:
              </Typography>
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px' }}>
                Genomic Screening
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 600 }}>
                Test being ordered:
              </Typography>
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px' }}>
                descendantsDNA Whole Genome
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 600 }}>
                Type of Specimen:
              </Typography>
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px' }}>
                Saliva
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 600 }}>
                Collection date:
              </Typography>
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px' }}>
                {todayFormatted}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px', fontWeight: 600 }}>
                Physician notes:
              </Typography>
              <BaseTextareaAutosize
                hasMaxLengthLabel={false}
                placeholder="Add any relevant clinical notes..."
                value={formData.physicianNotes}
                onChange={(e) => handleChange('physicianNotes', e.target.value)}
                inputStyles={{
                  width: '100%',
                  background: 'white',
                  border: '1px solid #ECEFF1',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  marginTop: '4px'
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Patient Eligibility Checkboxes */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h6"
          sx={{
            color: '#46545F',
            fontSize: '16px',
            fontWeight: 600,
            mb: 2
          }}
        >
          Patient Eligibility <span style={{ color: '#E73E3E' }}>*</span>
        </Typography>
        
        <Box
          sx={{
            p: 2,
            pl: 3,
            borderRadius: '8px',
            border: (errors.noBoneMarrowTransplant || errors.noRecentBloodTransfusion) ? '1px solid #E73E3E' : '1px solid #ECEFF1',
            background: '#F6F9FC'
          }}
        >
          <FormControlLabel
            control={
              <BaseCheckbox
                checked={formData.noBoneMarrowTransplant}
                onChange={(e) => handleChange('noBoneMarrowTransplant', e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px' }}>
                Patient has never had a bone marrow transplant <span style={{ color: '#E73E3E' }}>*</span>
              </Typography>
            }
            sx={{ 
              mb: 2,
              alignItems: 'center'
            }}
          />
          
          <FormControlLabel
            control={
              <BaseCheckbox
                checked={formData.noRecentBloodTransfusion}
                onChange={(e) => handleChange('noRecentBloodTransfusion', e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#46545F', fontSize: '14px' }}>
                Patient has not had a blood transfusion in the last 3 weeks <span style={{ color: '#E73E3E' }}>*</span>
              </Typography>
            }
            sx={{ 
              alignItems: 'center'
            }}
          />
          
          {(errors.noBoneMarrowTransplant || errors.noRecentBloodTransfusion) && (
            <Typography variant="caption" sx={{ color: '#E73E3E', fontSize: '12px', display: 'block', mt: 1 }}>
              Both eligibility confirmations are required to proceed
            </Typography>
          )}
          
          <Typography
            variant="caption"
            sx={{
              color: '#46545F',
              fontSize: '12px',
              display: 'block',
              mt: 2,
              fontStyle: 'italic'
            }}
          >
            These conditions affect the accuracy of genetic testing results. The test cannot be ordered if either condition applies.
          </Typography>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
          flexWrap: 'wrap'
        }}
      >
        <BaseButton
          type="button"
          onClick={onBack}
          sx={{
            width: { xs: '100%', sm: '140px' },
            background: '#46545F',
            '&:hover': {
              background: '#2A3342'
            }
          }}
        >
          Back
        </BaseButton>
        <Tooltip
          title={!canProceed ? "Please complete all required fields and confirm patient eligibility to proceed." : ""}
          arrow
          placement="top"
        >
          <span style={{ width: '100%', maxWidth: '180px' }}>
            <BaseButton
              onClick={handleNext}
              disabled={!canProceed}
              buttonType="primary"
              sx={{
                width: '100%',
                opacity: canProceed ? 1 : 0.5,
                cursor: canProceed ? 'pointer' : 'not-allowed',
                '&:disabled': {
                  background: '#ABB7C0',
                  color: 'white',
                  cursor: 'not-allowed'
                }
              }}
            >
              Continue
            </BaseButton>
          </span>
        </Tooltip>
      </Box>

      {/* Underage Patient Modal */}
      <Modal
        open={showUnderageModal}
        onClose={() => setShowUnderageModal(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: { xs: '100%', sm: '400px' },
            maxWidth: '90vw',
            borderRadius: '16px',
            background: 'white',
            padding: { xs: '24px 16px', sm: '32px' },
            outline: 'none',
            textAlign: 'center'
          }}
        >
          {/* Close button */}
          <Box
            sx={{
              position: 'absolute',
              right: '16px',
              top: '16px',
              cursor: 'pointer'
            }}
            onClick={() => setShowUnderageModal(false)}
          >
            <CloseIcon sx={{ width: 24, height: 24, color: '#46545F' }} />
          </Box>

          {/* Icon */}
          <Box
            sx={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: '#FFF3E0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}
          >
            <Typography sx={{ fontSize: '32px' }}>
              &#9888;
            </Typography>
          </Box>

          {/* Title */}
          <Typography
            variant="h2"
            sx={{
              color: '#E65100',
              fontSize: '20px',
              fontWeight: 600,
              mb: 2
            }}
          >
            Patient Too Young
          </Typography>

          {/* Message */}
          <Typography
            variant="body1"
            sx={{
              color: '#46545F',
              fontSize: '14px',
              mb: 3,
              lineHeight: 1.6
            }}
          >
            Sorry, this service is only available for patients who are 18 years of age or older.
            The patient's date of birth indicates they are under 18.
          </Typography>

          {/* Button */}
          <BaseButton
            type="button"
            onClick={() => setShowUnderageModal(false)}
            buttonType="primary"
            sx={{ minWidth: '140px' }}
          >
            OK
          </BaseButton>
        </Box>
      </Modal>
    </Box>
  );
}
